// US-SETUP-04 -- Add a guardian to my family.
//
// Reachable from the guardian's home screen via the "Add guardian"
// button in the Family card. The new guardian is an equal peer of every
// other guardian; the initial-setup marker has no authority implications.

import {
  type AccountListEntry,
  type AppError,
  type GuardianAddResult,
  type GuardianTypeEntry,
  accountsList,
  guardianAdd,
  guardianTypesList,
} from '@chorepop/core/client';
import { type Component, For, Show, createSignal, onMount } from 'solid-js';
import { ActionScreen } from '../components/ActionScreen';

type Props = {
  sessionId: string;
  familyCurrencyCode?: 'GBP' | 'USD';
  onCreated: (guardian: GuardianAddResult) => void;
  onCancel: () => void;
};

export const AddGuardianScreen: Component<Props> = (props) => {
  const [displayName, setDisplayName] = createSignal('');
  const [password, setPassword] = createSignal('');
  const [passwordConfirm, setPasswordConfirm] = createSignal('');
  const [submitting, setSubmitting] = createSignal(false);
  const [errorMessage, setErrorMessage] = createSignal<string | null>(null);
  const [familyLoadError, setFamilyLoadError] = createSignal<string | null>(null);
  const [loadingFamily, setLoadingFamily] = createSignal(true);
  const [children, setChildren] = createSignal<AccountListEntry[]>([]);
  const [guardianTypes, setGuardianTypes] = createSignal<GuardianTypeEntry[]>([]);
  const [relationshipByChild, setRelationshipByChild] = createSignal<Record<number, string>>({});

  onMount(async () => {
    try {
      const [accountsResult, guardianTypesResult] = await Promise.all([
        accountsList(),
        guardianTypesList(),
      ]);
      if (!accountsResult.ok) throw new Error(accountsResult.error.message);
      if (!guardianTypesResult.ok) throw new Error(guardianTypesResult.error.message);

      const activeChildren = accountsResult.value.filter((account) => account.roleCode === 'child');
      setChildren(activeChildren);
      setGuardianTypes(guardianTypesResult.value);
      setRelationshipByChild(
        Object.fromEntries(activeChildren.map((child) => [child.accountId, 'parent'])),
      );
    } catch {
      setFamilyLoadError(
        "We couldn't load the children and relationship choices. Please go back and try again.",
      );
    } finally {
      setLoadingFamily(false);
    }
  });

  const handleSubmit = async (e: Event) => {
    e.preventDefault();
    setErrorMessage(null);

    if (loadingFamily()) return;
    if (familyLoadError()) {
      setErrorMessage(familyLoadError());
      return;
    }

    const trimmedName = displayName().trim();
    if (trimmedName.length === 0) {
      setErrorMessage('Please enter a name for the guardian.');
      return;
    }
    if (password().length < 10) {
      setErrorMessage(`Password must be at least 10 characters (was ${password().length}).`);
      return;
    }
    if (password() !== passwordConfirm()) {
      setErrorMessage('The two password fields do not match.');
      return;
    }

    setSubmitting(true);
    try {
      const result = await guardianAdd({
        sessionId: props.sessionId,
        displayName: trimmedName,
        password: password(),
        displayCurrencyCode: props.familyCurrencyCode ?? 'GBP',
        childRelationships: children().map((child) => ({
          childAccountId: child.accountId,
          guardianTypeCode: relationshipByChild()[child.accountId] ?? 'parent',
        })),
      });
      if (!result.ok) {
        setErrorMessage(humanReadableError(result.error));
        return;
      }
      setPassword('');
      setPasswordConfirm('');
      setDisplayName('');
      props.onCreated(result.value);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ActionScreen
      eyebrow="Your family"
      title="Add a guardian"
      onBack={props.onCancel}
      footer={
        <button
          type="submit"
          form="add-guardian-form"
          class="primary action-screen-primary"
          disabled={submitting() || loadingFamily() || familyLoadError() !== null}
          data-testid="add-guardian-submit"
        >
          {submitting() ? 'Adding…' : 'Add guardian'}
        </button>
      }
    >
      <p class="action-screen-intro">
        Create another guardian account on this device. They'll be able to add children, approve
        deposits, and everything else you can do. They'll sign in with their own password from the
        account selection screen.
      </p>

      <form id="add-guardian-form" onSubmit={handleSubmit} novalidate class="action-form">
        <label class="field">
          <span class="field-label">Guardian's name</span>
          <input
            type="text"
            autocomplete="off"
            required
            autofocus
            disabled={submitting()}
            value={displayName()}
            onInput={(e) => {
              setDisplayName(e.currentTarget.value);
              setErrorMessage(null);
            }}
            data-testid="add-guardian-name"
          />
        </label>

        <Show when={children().length > 0}>
          <fieldset class="guardian-relationship-fieldset">
            <legend>Relationship to each child</legend>
            <p class="field-hint">
              Choose how this new guardian is related to each child. You can change these later.
            </p>
            <div class="guardian-relationship-grid">
              <For each={children()}>
                {(child) => (
                  <label class="field">
                    <span class="field-label">Relationship to {child.displayName}</span>
                    <select
                      disabled={submitting()}
                      value={relationshipByChild()[child.accountId] ?? 'parent'}
                      onChange={(event) =>
                        setRelationshipByChild((current) => ({
                          ...current,
                          [child.accountId]: event.currentTarget.value,
                        }))
                      }
                      data-testid={`add-guardian-relationship-${child.accountId}`}
                    >
                      <For each={guardianTypes()}>
                        {(type) => <option value={type.typeCode}>{type.typeLabel}</option>}
                      </For>
                    </select>
                  </label>
                )}
              </For>
            </div>
          </fieldset>
        </Show>

        <Show when={loadingFamily()}>
          <p class="form-hint" role="status">
            Loading family relationships…
          </p>
        </Show>

        <Show when={familyLoadError()}>
          {(message) => (
            <div class="form-error" role="alert">
              {message()}
            </div>
          )}
        </Show>

        <label class="field">
          <span class="field-label">Password</span>
          <input
            type="password"
            autocomplete="new-password"
            required
            disabled={submitting()}
            value={password()}
            onInput={(e) => {
              setPassword(e.currentTarget.value);
              setErrorMessage(null);
            }}
            data-testid="add-guardian-password"
          />
          <span class="field-hint">At least 10 characters.</span>
        </label>

        <label class="field">
          <span class="field-label">Confirm password</span>
          <input
            type="password"
            autocomplete="new-password"
            required
            disabled={submitting()}
            value={passwordConfirm()}
            onInput={(e) => {
              setPasswordConfirm(e.currentTarget.value);
              setErrorMessage(null);
            }}
            data-testid="add-guardian-password-confirm"
          />
        </label>

        <Show when={errorMessage()}>
          {(msg) => (
            <div class="form-error" role="alert">
              {msg()}
            </div>
          )}
        </Show>
      </form>
    </ActionScreen>
  );
};

function humanReadableError(error: AppError): string {
  switch (error.kind) {
    case 'validation':
      return error.message;
    case 'conflict':
      return error.message;
    case 'unauthorized':
      return 'Your session has expired. Please sign in again.';
    case 'network_required':
      return error.message;
    default:
      return `Something went wrong: ${error.message}`;
  }
}
